import "@/App.css";
import "@/lib/i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/components/AppLayout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Students from "@/pages/Students";
import Classes from "@/pages/Classes";
import Grades from "@/pages/Grades";
import Attendance from "@/pages/Attendance";
import Fees from "@/pages/Fees";
import Accounting from "@/pages/Accounting";
import Users from "@/pages/Users";
import Setup from "@/pages/Setup";
import EmailLogs from "@/pages/EmailLogs";
import Souscription from "@/pages/Souscription";
import SubscriptionRequests from "@/pages/SubscriptionRequests";
import Notifications from "@/pages/Notifications";
import AdminPanel from "@/pages/AdminPanel";
import ParentPortal from "@/pages/ParentPortal";
import AcademicStructure from "@/pages/AcademicStructure";
import TeacherDashboard from "@/pages/TeacherDashboard";
import CourseWorkspace from "@/pages/CourseWorkspace";
import StudentDashboard from "@/pages/StudentDashboard";
import Timetable from "@/pages/Timetable";
import DirectorDashboard from "@/pages/DirectorDashboard";
import Messagerie from "@/pages/Messagerie";
import AdmissionsAdmin from "@/pages/AdmissionsAdmin";
import PublicApply from "@/pages/PublicApply";
import PublicApplicantPortal from "@/pages/PublicApplicantPortal";
import PublicSchool from "@/pages/PublicSchool";
import About from "@/pages/About";
import AuditLogs from "@/pages/AuditLogs";
import Permissions from "@/pages/Permissions";
import Gradebook from "@/pages/Gradebook";
import NotFound from "@/pages/NotFound";
import Panafrican from "@/pages/Panafrican";
import Agenda from "@/pages/Agenda";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground text-sm">Chargement…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function ParentGate({ children }) {
  const { user } = useAuth();
  if (user && user.role === "parent") return <Navigate to="/portail" replace />;
  return children;
}

function DashboardRouter() {
  const { user, hasModule } = useAuth();
  if (user?.role === "teacher" && !hasModule("finance")) return <Navigate to="/app/enseignant" replace />;
  if (user?.role === "student") return <Navigate to="/app/eleve" replace />;
  if (!hasModule("finance")) return <Navigate to="/app/messagerie" replace />;
  return <Dashboard />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/app"
              element={
                <Protected>
                  <ParentGate>
                    <AppLayout />
                  </ParentGate>
                </Protected>
              }
            >
              <Route index element={<DashboardRouter />} />
              <Route path="eleves" element={<Students />} />
              <Route path="classes" element={<Classes />} />
              <Route path="structure" element={<AcademicStructure />} />
              <Route path="enseignant" element={<TeacherDashboard />} />
              <Route path="cours/:courseId" element={<CourseWorkspace />} />
              <Route path="eleve" element={<StudentDashboard />} />
              <Route path="emploi-du-temps" element={<Timetable />} />
              <Route path="direction" element={<DirectorDashboard />} />
              <Route path="messagerie" element={<Messagerie />} />
              <Route path="admissions" element={<AdmissionsAdmin />} />
              <Route path="notes" element={<Grades />} />
              <Route path="presences" element={<Attendance />} />
              <Route path="finances" element={<Fees />} />
              <Route path="comptabilite" element={<Accounting />} />
              <Route path="utilisateurs" element={<Users />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="audit" element={<AuditLogs />} />
              <Route path="permissions" element={<Permissions />} />
              <Route path="bulletin" element={<Gradebook />} />
              <Route path="admin" element={<AdminPanel />} />
              <Route path="panafricain" element={<Panafrican />} />
              <Route path="agenda" element={<Agenda />} />
              <Route path="setup" element={<Setup />} />
              <Route path="envois-bulletins" element={<EmailLogs />} />
              <Route path="demandes" element={<SubscriptionRequests />} />
            </Route>
            <Route path="/souscription" element={<Souscription />} />
            <Route
              path="/portail"
              element={
                <Protected>
                  <ParentPortal />
                </Protected>
              }
            />
            <Route path="/a-propos" element={<About />} />
            <Route path="/about" element={<About />} />
            <Route path="/candidater" element={<PublicApply />} />
            <Route path="/candidater/:schoolId" element={<PublicApply />} />
            <Route path="/candidature/:token" element={<PublicApplicantPortal />} />
            <Route path="/ecoles/:slug" element={<PublicSchool />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
